import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  LoaderCircleV2,
  OKELink,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SM_DF,
  UI_CAPTION_DF,
  colors2,
  icons,
} from "oolib";
// ButtonGhost is NOT exported from the oolib root — only from the v2 path
// (same import the chat root uses). A root import silently resolves to
// undefined and crashes the render.
import { ButtonGhost } from "oolib/dist/v2/components/Buttons/index.js";
import { AIMessageStyled } from "../AI/styled";
import { MarkdownRenderer } from "../AI/index";
import { StatusLineStyled, TrailStyled, StreamedAnswerStyled } from "./styled";
import { FollowUpChips } from "../../FollowUpChips";
import { TopicFactsStrip } from "../../TopicFactsStrip";

const { CaretRight } = icons;

// ---------------------------------------------------------------------------
// Event → user language
// ---------------------------------------------------------------------------

// Tool names are engineering vocabulary; the trail speaks the product's.
const TOOL_LABELS = {
  searchAnnotation: "Searched highlights",
  searchChunks: "Searched text",
  getTagPrevalence: "Counted tags",
  getTagCooccurrence: "Mapped tag relationships",
  aggregateMetricsAcrossDimensions: "Broke down by category",
  getCorrelationBetweenDimensions: "Cross-tabulated categories",
  aggregateMetricsAcrossTime: "Charted over time",
  getSingleMetric: "Calculated a metric",
  getDocumentCount: "Counted documents",
  fetchStructuredFieldData: "Read structured fields",
  listTopics: "Read the topics",
  getTopic: "Read a topic's observations",
};
const toolLabel = (name) => TOOL_LABELS[name] || name;

// Backend phase names → status-line copy, written the way a researcher
// would narrate their own process — not engine stages. Honest (each maps
// to what is really happening) but human.
const PHASE_STATUS = {
  "Loading chat": "Reading your question…",
  "Contextualizing query": "Reading your question…",
  "Loading schema": "Getting familiar with your data…",
  "Extracting filters from query": "Noting what to focus on…",
  "Loading tools": "Getting familiar with your data…",
  "Planning gathering strategy": "Thinking about where the answer might live…",
  "Composing response": "Writing up what I found…",
};

const INITIAL_STREAM = {
  active: false,
  done: false,
  stopped: false,
  error: null,
  statusText: "",
  steps: [], // [{ iteration, reasoning, tools: [{name, description, resultSummary, hasError}] }]
  toolCount: 0,
  answerText: "",
  complete: null, // the `complete` event payload
  startedAt: null,
};

function applyEvent(prev, event) {
  switch (event.type) {
    case "init":
      return { ...prev, statusText: "Reading your question…" };

    case "phase_start": {
      const mapped = PHASE_STATUS[event.phase];
      return mapped ? { ...prev, statusText: mapped } : prev;
    }

    case "plan":
      return { ...prev, statusText: "Looking through your data…" };

    case "gather_start": {
      const steps = [...prev.steps];
      if (!steps.find((s) => s.iteration === event.iteration)) {
        steps.push({ iteration: event.iteration, reasoning: "", tools: [] });
      }
      // Deliberately no "step N" — iteration count is engine mechanics, not
      // something the user needs. The trail speaks in activities instead.
      return { ...prev, steps, statusText: "Digging into the data…" };
    }

    case "agent_reasoning": {
      const steps = prev.steps.map((s) =>
        s.iteration === event.iteration ? { ...s, reasoning: event.reasoning || "" } : s
      );
      return { ...prev, steps };
    }

    case "gather_iteration": {
      const toolCalls = event.toolCalls || [];
      const steps = prev.steps.map((s) =>
        s.iteration === event.iteration ? { ...s, tools: [...s.tools, ...toolCalls] } : s
      );
      const last = toolCalls[toolCalls.length - 1];
      const statusText = last
        ? `${toolLabel(last.name)} · ${last.resultSummary || ""}`
        : prev.statusText;
      return {
        ...prev,
        steps,
        toolCount: prev.toolCount + toolCalls.length,
        statusText,
      };
    }

    case "gather_complete":
      return { ...prev, statusText: "Pulling the findings together…" };

    case "answer_delta":
      return { ...prev, answerText: prev.answerText + (event.text || "") };

    case "complete":
      return {
        ...prev,
        done: true,
        active: false,
        // Non-streaming fallback path delivers the whole answer here
        answerText: prev.answerText || event.answer || "",
        complete: event,
      };

    case "error":
      return { ...prev, active: false, error: event.message || "Something went wrong." };

    default:
      return prev;
  }
}

// ---------------------------------------------------------------------------
// Hook — consumes POST /api/aiChat/getResponseSSE/:chatId
// ---------------------------------------------------------------------------

export const useStreamedAskAI = () => {
  const [stream, setStream] = useState(INITIAL_STREAM);
  const abortRef = useRef(null);
  const lastParamsRef = useRef(null);

  const start = useCallback(async (params) => {
    const { query, chatId, activeLang, immutableActiveFilters, immutableContentTypes, aiChatAgent, anchor, onComplete, onError } = params;
    lastParamsRef.current = params;

    abortRef.current?.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;

    setStream({ ...INITIAL_STREAM, active: true, statusText: "Starting…", startedAt: Date.now() });

    try {
      // fetch (not axios) — axios buffers the whole body; streaming needs the
      // reader. Auth rides the same headers the axios instance already carries.
      const res = await fetch(`${axios.defaults.baseURL || ""}/api/aiChat/getResponseSSE/${chatId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(axios.defaults.headers.common || {}) },
        credentials: "include",
        body: JSON.stringify({
          query,
          activeLang,
          // Topic anchor rides on EVERY message (not only the first): a
          // follow-up chip or a cleared pill changes the anchor mid-chat.
          ...(anchor ? { anchor } : {}),
          ...(chatId === "new"
            ? {
                immutableActiveFilters: immutableActiveFilters || null,
                immutableContentTypes: immutableContentTypes || null,
                aiChatAgent,
              }
            : {}),
        }),
        signal: abortController.signal,
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.error || errData?.message || `Request failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let completeEvent = null;
      let errored = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop();
        for (const frame of frames) {
          const line = frame.trim();
          if (!line.startsWith("data: ")) continue;
          let event;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue; // malformed frame — skip
          }
          setStream((prev) => applyEvent(prev, event));
          if (event.type === "complete") completeEvent = event;
          if (event.type === "error") errored = event.message;
        }
      }

      if (completeEvent) onComplete?.(completeEvent);
      else if (errored) onError?.(errored);
      else {
        // Connection ended without a terminal event
        setStream((prev) =>
          prev.done ? prev : { ...prev, active: false, error: "The connection dropped before the answer finished." }
        );
        onError?.("connection dropped");
      }
    } catch (err) {
      if (err.name === "AbortError") return; // stop() / switch handled it
      setStream((prev) => ({ ...prev, active: false, error: err.message }));
      onError?.(err.message);
    } finally {
      if (abortRef.current === abortController) abortRef.current = null;
    }
  }, []);

  /** User-facing stop: keeps the partial answer, marks the message stopped. */
  const stop = useCallback(() => {
    abortRef.current?.abort();
    setStream((prev) =>
      prev.active ? { ...prev, active: false, stopped: true, statusText: "" } : prev
    );
  }, []);

  /** Silent abort + reset — for chat switches and unmount. */
  const reset = useCallback(() => {
    abortRef.current?.abort();
    setStream(INITIAL_STREAM);
  }, []);

  const retry = useCallback(() => {
    if (lastParamsRef.current) start(lastParamsRef.current);
  }, [start]);

  useEffect(() => () => abortRef.current?.abort(), []);

  return { stream, start, stop, reset, retry };
};

// ---------------------------------------------------------------------------
// Presentation
// ---------------------------------------------------------------------------

// Result-count units per tool, for the grouped summary ("52 excerpts").
const TOOL_UNITS = {
  searchAnnotation: "excerpts",
  searchChunks: "passages",
};

/**
 * Collapse the raw tool-call list into one line per activity type:
 * "Searched highlights — 6 searches · 52 excerpts". Iteration structure
 * ("step 1 / step 2") is engine mechanics and is deliberately not shown —
 * the developer playground keeps the full per-iteration log. Skipped/failed
 * lookups aggregate into a single muted line rather than red error rows.
 */
function groupActivities(steps) {
  const groups = new Map(); // toolName → { calls, results, order }
  let skipped = 0;
  let order = 0;
  for (const step of steps) {
    for (const tool of step.tools) {
      if (tool.hasError) {
        skipped++;
        continue;
      }
      if (!groups.has(tool.name)) groups.set(tool.name, { calls: 0, results: 0, order: order++ });
      const g = groups.get(tool.name);
      g.calls++;
      const m = /^(\d+)/.exec(tool.resultSummary || "");
      if (m) g.results += parseInt(m[1], 10);
    }
  }
  const lines = [...groups.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([name, g]) => {
      const unit = TOOL_UNITS[name];
      const results = unit && g.results > 0 ? ` · ${g.results} ${unit}` : "";
      const calls = g.calls > 1 ? ` — ${g.calls} lookups` : "";
      return `${toolLabel(name)}${calls}${results}`;
    });
  return { lines, skipped };
}

const InvestigationTrail = ({ stream }) => {
  const [expanded, setExpanded] = useState(false);
  const { steps, toolCount, done, startedAt, complete } = stream;
  if (steps.length === 0) return null;

  const elapsedS = complete?.elapsedSeconds
    ? Math.round(Number(complete.elapsedSeconds))
    : startedAt
    ? Math.round((Date.now() - startedAt) / 1000)
    : null;

  const label = done
    ? `Investigated · ${toolCount} lookups${elapsedS ? ` · ${elapsedS}s` : ""}`
    : `Investigating${toolCount ? ` · ${toolCount} lookups` : "…"}`;

  const { lines, skipped } = groupActivities(steps);

  return (
    <TrailStyled expanded={expanded}>
      <button
        type="button"
        className="trail-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="trail-caret">
          <CaretRight size={12} weight="bold" />
        </span>
        <UI_CAPTION_DF>{label}</UI_CAPTION_DF>
      </button>

      {expanded && (
        <div className="trail-steps">
          <div className="trail-step-tools">
            {lines.map((line, i) => (
              <div key={i} className="trail-tool-line">
                <UI_CAPTION_DF>{line}</UI_CAPTION_DF>
              </div>
            ))}
            {skipped > 0 && (
              <div className="trail-tool-line">
                <UI_CAPTION_DF style={{ color: colors2.grey40 }}>
                  Skipped {skipped === 1 ? "one lookup" : `${skipped} lookups`} — nothing to search there
                </UI_CAPTION_DF>
              </div>
            )}
          </div>
        </div>
      )}
    </TrailStyled>
  );
};

/**
 * The in-flight assistant message: live status while investigating, the
 * collapsible trail once steps exist, and the answer rendering token by
 * token as compose streams it. Swapped for the stored message (references,
 * feedback, charts) once the chat refetches.
 */
/**
 * @param autoScroll  follow the stream by scrolling the page. Right in the
 *   chat, where the bubble owns the column. Wrong anywhere the bubble is a
 *   panel on a larger page — the dashboard's Ask AI widget scrolls inside
 *   its own card, and scrolling the page under it on every token reads as
 *   the layout jumping about.
 */
export const StreamingAIMessage = ({ stream, onStop, onRetry, onFollowUp, autoScroll = true }) => {
  const rootRef = useRef(null);
  const writing = stream.active && stream.answerText.length > 0;

  // Follow the stream — but only when the reader is already at the bottom,
  // so scrolling up to read is never hijacked.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || !autoScroll) return;
    const distanceFromViewportBottom = node.getBoundingClientRect().bottom - window.innerHeight;
    if (distanceFromViewportBottom > -40 && distanceFromViewportBottom < 400) {
      node.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [stream.statusText, stream.answerText, stream.error, stream.stopped, autoScroll]);

  // The default bubble hugs its content (fit-content) — right for a finished
  // answer, wrong for a message that starts as one status line and grows into
  // paragraphs: it renders as a cramped sliver, then judders wider with every
  // token. Streamed messages claim their full column up front.
  const bubbleStyle = { width: "100%" };

  if (stream.error) {
    return (
      <AIMessageStyled ref={rootRef} style={bubbleStyle}>
        <UI_BODY_SM_DF>The answer couldn't be completed. {stream.error}</UI_BODY_SM_DF>
        <UI_CAPTION_DF>
          <OKELink onClick={onRetry}>Try again</OKELink>
        </UI_CAPTION_DF>
      </AIMessageStyled>
    );
  }

  return (
    <AIMessageStyled ref={rootRef} style={bubbleStyle}>
      {/* Live status — replaced by the answer the moment text starts */}
      {stream.active && !writing && (
        <StatusLineStyled>
          <span className="status-spinner">
            <LoaderCircleV2 S isBlock />
          </span>
          <span className="status-text">
            <UI_BODY_SEMIBOLD_SM_DF aria-live="polite">{stream.statusText}</UI_BODY_SEMIBOLD_SM_DF>
          </span>
          {/* Stop control temporarily disabled — needs UX for "backend keeps
              writing after stop". Handler plumbing (onStop) stays wired.
          {onStop && (
            <span style={{ marginLeft: "auto" }}>
              <ButtonGhost S onClick={onStop}>
                Stop
              </ButtonGhost>
            </span>
          )} */}
        </StatusLineStyled>
      )}

      <InvestigationTrail stream={stream} />

      {stream.answerText ? (
        <StreamedAnswerStyled streaming={stream.active}>
          <UI_BODY_SM_DF>
            <div className="md-stream">
              <div>
                <MarkdownRenderer>{stream.answerText}</MarkdownRenderer>
              </div>
            </div>
          </UI_BODY_SM_DF>
          {/* Stop control temporarily disabled — see note above.
          {writing && onStop && (
            <div style={{ marginTop: "0.6rem" }}>
              <ButtonGhost S onClick={onStop}>
                Stop
              </ButtonGhost>
            </div>
          )} */}
        </StreamedAnswerStyled>
      ) : null}

      {stream.stopped && (
        <UI_CAPTION_DF style={{ color: colors2.grey60 }}>
          Stopped. The full answer is still being written and will appear in this chat shortly.
        </UI_CAPTION_DF>
      )}

      {/* Same footer rhythm as the stored message (facts, then chips); the
          references and actions arrive with the stored message on refetch. */}
      {stream.done && !stream.active && (stream.complete?.topicFacts || stream.complete?.followUps?.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem", marginTop: "0.8rem", paddingTop: "0.8rem", borderTop: `1px solid ${colors2.grey10}` }}>
          {stream.complete?.topicFacts && <TopicFactsStrip facts={stream.complete.topicFacts} />}
          <FollowUpChips followUps={stream.complete?.followUps} onPick={onFollowUp} />
        </div>
      )}
    </AIMessageStyled>
  );
};
